import {
  BoltIcon,
  DatesDisplay,
  Flex,
  LocationDisplay,
  Map,
  ProgressCircle,
  SimpleGrid,
  Spacer,
  StatusBadge,
  Text,
  UserAvatar,
  ViewSelector,
  ViewType,
} from 'components'
import { useWorkspace } from 'components/providers'
import { startCase } from 'lodash-es'
import { AnimatePresence, motion } from 'motion/react'
import { useEffect, useState } from 'react'
import { generatePath, useLocation, useNavigate, useParams } from 'react-router'
import { useLocalStorage } from 'usehooks-ts'
import { useBreakpoint, useTheme, useUser } from 'utils'
import noEventDark from '../assets/emptyStates/no_events_dark.svg'
import noEventLight from '../assets/emptyStates/no_events_light.svg'
import { EventStatusFilter, EventStatusFilterValue, StatTile } from '../components'
import CreateNewButton from '../components/CreateNewButton'
import EmptyState from '../components/EmptyState'
import { EventsSection } from '../components/event'
import ResourceUpsell from '../components/subscription/ResourceUpsell'
import { EventRecord, EventStatus, getEventsByStatus, useEvents } from '../utils/eventUtils'
import { RoutePath } from '../utils/routing'

export default function EventsPage() {
  const allWorkspaces = useParams().workspaceId === 'all'
  const { events = [], status } = useEvents()
  const { subscription } = useWorkspace()
  const { plan, selectedResource } = subscription || {}
  const { theme } = useTheme()
  const { hash } = useLocation()
  const isMobile = useBreakpoint() < 2
  const [displayMode, setDisplayMode] = useLocalStorage<ViewType>('event-display-mode', 'grid')
  const [statusFilter, setStatusFilter] = useState<EventStatusFilterValue>('all')

  const eventsByStatus = getEventsByStatus(events)
  const totalCount = Object.values(eventsByStatus).reduce((acc, cur) => acc + cur.length, 0)
  const filteredEvents =
    statusFilter === 'all' ? events : eventsByStatus[statusFilter as EventStatus]
  const filteredCount = filteredEvents.length

  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.replace('#', ''))
      if (el) el.scrollIntoView({ behavior: 'smooth' })
    }
  }, [hash])

  useEffect(() => {
    if (isMobile) setDisplayMode('grid')
  }, [isMobile])

  if (plan === 'basic' && selectedResource === 'facility') {
    return (
      <Flex flexDirection="column" gap={4} mx={{ base: 4, md: 'auto' }}>
        <Text textStyle="h3" pt={4}>
          Events
        </Text>
        <ResourceUpsell variant="two" resourceName="event" />
      </Flex>
    )
  }

  return (
    <Flex
      direction="column"
      gap={4}
      flex="auto"
      p={isMobile ? 4 : 0}
      pb={3}
      pr={5}
      overflowY="auto"
      overflowX="hidden"
    >
      <Flex align="flex-end">
        <Text textStyle="h3" pt={2}>
          Events
        </Text>
        <Spacer />
        {!isMobile && (
          <Flex align="center" gap={3}>
            <EventStatusFilter
              counts={{
                live: eventsByStatus.live.length,
                upcoming: eventsByStatus.upcoming.length,
                past: eventsByStatus.past.length,
              }}
              value={statusFilter}
              onChange={setStatusFilter}
            />
            <ViewSelector
              options={['grid', 'list', 'map']}
              value={displayMode}
              onChange={setDisplayMode}
            />
          </Flex>
        )}
      </Flex>
      {status === 'pending' ? (
        <Flex p={20} justifyContent="center" maxW="100%">
          <ProgressCircle isIndeterminate />
        </Flex>
      ) : filteredCount === 0 ? (
        <Flex align="center" flex={1} justify="center">
          <EmptyState
            title={
              statusFilter === 'all'
                ? "Nothing on the calendar—let's fix that!"
                : `No ${startCase(statusFilter)} Events`
            }
            description={
              statusFilter === 'all' ? 'Create an event and get your community engaged.' : undefined
            }
            iconProps={{ height: '176px' }}
            iconSrc={theme === 'light' ? noEventLight : noEventDark}
            button={statusFilter === 'all' ? <CreateNewButton /> : undefined}
          />
        </Flex>
      ) : (
        <>
          {isMobile && events.length != 0 && (
            <Flex width="100%" direction="column" gap={4}>
              <SimpleGrid columns={2} gap={4}>
                <StatTile Icon={BoltIcon} label="Total" num={totalCount} minW="160px" />
                <StatTile
                  Icon={BoltIcon}
                  label="Live"
                  num={eventsByStatus.live.length}
                  minW="160px"
                />
                <StatTile
                  Icon={BoltIcon}
                  label="Upcoming"
                  num={eventsByStatus.upcoming.length}
                  minW="160px"
                />
                <StatTile
                  Icon={BoltIcon}
                  label="Past"
                  num={eventsByStatus.past.length}
                  minW="160px"
                />
              </SimpleGrid>
            </Flex>
          )}

          {displayMode === 'map' ? (
            <ViewRenderer
              events={statusFilter === 'all' ? events : eventsByStatus[statusFilter]}
              isMobile={isMobile}
              mapView
              showWorkspace={allWorkspaces}
            />
          ) : (
            <AnimatePresence mode="wait">
              <motion.div
                key={statusFilter}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                initial={{ opacity: 0, y: 6 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
              >
                <ViewRenderer
                  events={statusFilter === 'all' ? events : eventsByStatus[statusFilter]}
                  isMobile={isMobile}
                  mapView={false}
                  showWorkspace={allWorkspaces}
                />
              </motion.div>
            </AnimatePresence>
          )}
        </>
      )}
    </Flex>
  )
}

interface ViewRendererProps {
  events: EventRecord[]
  mapView: boolean
  isMobile: boolean
  showWorkspace: boolean
}

function ViewRenderer({ events, mapView, isMobile, showWorkspace }: ViewRendererProps) {
  const navigate = useNavigate()

  function goToEvent(id: string, workspaceId: string) {
    navigate(generatePath(RoutePath.EventInfo, { workspaceId: workspaceId!, id }))
  }

  if (mapView) {
    const locations = events.map(event => ({
      lat: event.location.lat,
      lng: event.location.lng,
      info: event,
    }))

    return (
      <Map
        //@ts-ignore
        data={locations}
        onMarkerClick={marker => goToEvent(marker.info?.id!, marker.info?.workspaceId!)}
        markers={events.map(event => (
          <MarkerInfoCard key={event.id} {...event} />
        ))}
      />
    )
  }

  const eventsByStatus = getEventsByStatus(events)

  return (
    <Flex direction="column" gap={5}>
      {Object.entries(eventsByStatus).map(([status, events]) => {
        if (events.length === 0) return null

        const s = status as EventStatus
        let nextSection = false
        if (s === 'live' && eventsByStatus.upcoming.length > 0) nextSection = true
        if (s === 'upcoming' && eventsByStatus.past.length > 0) nextSection = true

        return (
          <EventsSection
            key={s}
            events={events}
            status={s}
            showWorkspace={showWorkspace}
            nextSection={nextSection}
            mobileView={isMobile}
          />
        )
      })}
    </Flex>
  )
}

function MarkerInfoCard(props: EventRecord) {
  const { managerId, imageUrl, status, startTime, endTime, location, title } = props
  const user = useUser(managerId)

  return (
    <Flex gap={5} w="400px">
      <Flex
        bgImage={`url(${imageUrl})`}
        bgSize="cover"
        width="175px"
        height="115px"
        borderRadius="md"
        bgPos="center"
        position="relative"
      >
        <StatusBadge
          position="absolute"
          bottom={2}
          left="unset"
          right={2}
          text={startCase(status)}
          iconVisible={status === 'live'}
        />
      </Flex>
      <Flex direction="column" gap={2} color="black" maxW="200px">
        <Text textStyle="bodyMedium">{title}</Text>
        {startTime && (
          <DatesDisplay
            startTime={startTime}
            endTime={endTime}
            textProps={{ opacity: 1 }}
            gap={3}
          />
        )}
        <LocationDisplay location={location} containerProps={{ gap: 3 }} />

        {user && (
          <Flex align="center" gap={2}>
            <UserAvatar user={user} />
            <Text textStyle="bodyXSmall">{user.displayName}</Text>
          </Flex>
        )}
      </Flex>
    </Flex>
  )
}
